import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import {
    Badge,
    Box,
    Button,
    LinearProgress,
    Stack,
    Tab,
    Tabs,
    Typography
} from "@mui/material";

import KeyboardBackspaceIcon from "@mui/icons-material/KeyboardBackspace";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import SyncOutlinedIcon from "@mui/icons-material/SyncOutlined";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";

import api from "../../Api/apiAxios";
import Loading from "../../Components/Loading";
import { ButtonBase } from "../Pedidos/styles";

import FiltrosIntegracao from "./FiltrosIntegracao";
import TabelaIntegracaoBma from "./TabelaIntegracaoBma";
import TabelaIntegracaoRevendas from "./TabelaIntegracaoRevendas";
import ModalClientesRevenda from "./ModalClientesRevenda";
import ModalInconsistencias from "./ModalInconsistencias";
import ModalLoteStatus from "./ModalLoteStatus";
import PopoverHistoricoOS from "./PopoverHistoricoOS";
import useSelecaoIntegracao from "./useSelecaoIntegracao";

import {
    atualizarCodigoIntegracao,
    gerarIntegracao,
    listarInconsistencias,
    listarIntegracaoBma,
    listarIntegracaoCanais,
    listarTiposServico,
    obterSituacaoPeriodo
} from "./integracaoApi";

import { BORDA_SUAVE, NAVY, SOMBRA_CARD, formatarPeriodoExtenso, mensagemErroApi } from "./utils";

const FILTROS_INICIAIS = {
    diaFaturamento: "",
    diaVencimento: "",
    numeroSerie: "",
    busca: "",
    status: ""
};

function passaFiltros(item, filtros, nome) {
    if (filtros.diaFaturamento !== "" && item.diaFaturamento !== Number(filtros.diaFaturamento))
        return false;

    if (filtros.diaVencimento !== "" && item.diaVencimento !== Number(filtros.diaVencimento))
        return false;

    if (filtros.status === "nao-gerado" && item.statusOS)
        return false;

    if (filtros.status && filtros.status !== "nao-gerado" && item.statusOS !== filtros.status)
        return false;

    if (filtros.busca && !(nome || "").toLowerCase().includes(filtros.busca.toLowerCase()))
        return false;

    if (filtros.numeroSerie) {
        const ns = filtros.numeroSerie.toLowerCase();
        const servicos = item.servicos || [];
        const temNumeroSerie = servicos.some((s) => "numeroSerie" in s);
        if (temNumeroSerie && !servicos.some((s) => (s.numeroSerie || "").toLowerCase().includes(ns)))
            return false;
    }

    return true;
}

const IntegracaoSisdm = () => {
    const navigate = useNavigate();
    const { periodo } = useParams();
    const location = useLocation();
    const pularAutoGeracaoRef = useRef(Boolean(location.state?.pularGeracaoAutomatica));

    const [aba, setAba] = useState("bma");
    const [dadosBma, setDadosBma] = useState([]);
    const [dadosCanais, setDadosCanais] = useState([]);
    const [tiposServico, setTiposServico] = useState([]);
    const [loading, setLoading] = useState(true);
    const [situacaoPeriodo, setSituacaoPeriodo] = useState(null);
    const [progressoGeracao, setProgressoGeracao] = useState(null);
    const gerandoRef = useRef(false);
    const [filtros, setFiltros] = useState(FILTROS_INICIAIS);

    const [inconsistencias, setInconsistencias] = useState([]);
    const [showInconsistencias, setShowInconsistencias] = useState(false);
    const [integrarAposInconsistencias, setIntegrarAposInconsistencias] = useState(false);

    const [lote, setLote] = useState(null);
    const [clientesRevenda, setClientesRevenda] = useState(null);
    const [historico, setHistorico] = useState(null);

    const usuarioNomeRef = useRef(null);
    const usuarioId = Number(window.localStorage.getItem("idUsuario") || 0) || null;

    function voltar() {
        navigate("/faturamentointegracao");
    }

    useEffect(() => {
        let ativo = true;

        async function carregarUsuario() {
            if (!usuarioId) return;
            try {
                const resp = await api.get(`/Usuario/${usuarioId}`);
                if (ativo) usuarioNomeRef.current = resp.data?.nome || null;
            } catch (e) {
                // Sem nome disponível o backend registra o usuário padrão.
            }
        }

        carregarUsuario();
        listarTiposServico()
            .then((itens) => ativo && setTiposServico(itens))
            .catch(() => {});

        return () => { ativo = false; };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const listarPorAba = useCallback(
        (abaAtual) => abaAtual === "bma" ? listarIntegracaoBma(periodo) : listarIntegracaoCanais(periodo),
        [periodo]
    );

    // As OS são geradas automaticamente em segundo plano: a listagem aparece primeiro
    // e cada linha completa ainda sem OS dispara o gerar por grupo, com barra de progresso.
    // Recebe "totalmenteIntegrado" como parâmetro (em vez de ler o state) para não recriar
    // esta função a cada recarga — o state mudaria de referência e reabriria o efeito de carga,
    // causando um loop de "carregando" infinito.
    const gerarPendentesEmSegundoPlano = useCallback(async (abaAtual, itens, totalmenteIntegrado) => {
        if (pularAutoGeracaoRef.current) return;

        const pendentes = itens.filter((item) => !item.osId && item.completo);
        if (pendentes.length === 0 || gerandoRef.current || totalmenteIntegrado) return;

        gerandoRef.current = true;
        setProgressoGeracao({ feitas: 0, total: pendentes.length });
        const erros = [];
        try {
            let feitas = 0;
            for (const item of pendentes) {
                try {
                    await gerarIntegracao({
                        periodo,
                        aba: abaAtual,
                        clienteId: abaAtual === "bma" ? item.clienteId : null,
                        distribuidorId: abaAtual === "canais" ? item.distribuidorId : null,
                        usuarioId,
                        usuario: usuarioNomeRef.current
                    });
                } catch (e) {
                    const nome = item.clienteNome || item.distribuidorNome;
                    erros.push(`${nome}: ${mensagemErroApi(e, "erro ao gerar a OS")}`);
                }
                feitas += 1;
                setProgressoGeracao({ feitas, total: pendentes.length });
            }
        } finally {
            gerandoRef.current = false;
            setProgressoGeracao(null);
        }

        if (erros.length > 0) {
            console.error(`Falha ao gerar OS automaticamente — ${erros.join(" • ")}`);
        }

        try {
            const atualizados = await listarPorAba(abaAtual);
            (abaAtual === "bma" ? setDadosBma : setDadosCanais)(atualizados);
        } catch (e) {
            // Mantém a listagem atual; os status atualizam na próxima recarga.
        }
    }, [periodo, usuarioId, listarPorAba]);

    const carregarDados = useCallback(async (abaAtual, mostrarCarregando = true) => {
        if (mostrarCarregando) setLoading(true);
        try {
            const itens = await listarPorAba(abaAtual);

            if (abaAtual === "bma") {
                setDadosBma(itens);
            } else {
                setDadosCanais(itens);
            }

            if (mostrarCarregando) {
                setInconsistencias(await listarInconsistencias(periodo, abaAtual));
            }

            const situacao = await obterSituacaoPeriodo(periodo).catch(() => null);
            setSituacaoPeriodo(situacao);

            if (mostrarCarregando) setLoading(false);

            await gerarPendentesEmSegundoPlano(abaAtual, itens, Boolean(situacao?.totalmenteIntegrado));
        } catch (e) {
            console.error(mensagemErroApi(e, "Erro ao carregar os dados da integração."));
            if (mostrarCarregando) setLoading(false);
        }
    }, [periodo, listarPorAba, gerarPendentesEmSegundoPlano]);

    useEffect(() => {
        let ativo = true;

        async function inicializar() {
            await carregarDados("bma", true);
            if (ativo) await carregarDados("canais", false);
        }

        inicializar();
        return () => { ativo = false; };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const abaAnteriorRef = useRef(null);

    useEffect(() => {
        if (abaAnteriorRef.current === null) {
            abaAnteriorRef.current = aba;
            return;
        }
        abaAnteriorRef.current = aba;
        carregarDados(aba, true);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [aba]);

    const itens = aba === "bma" ? dadosBma : dadosCanais;

    const itensFiltrados = useMemo(
        () => itens.filter((item) =>
            passaFiltros(item, filtros, aba === "bma" ? item.clienteNome : item.distribuidorNome)),
        [itens, filtros, aba]
    );

    const selecao = useSelecaoIntegracao(itensFiltrados);
    const { itensSelecionados, limpar } = selecao;

    const somenteLeitura = Boolean(situacaoPeriodo?.totalmenteIntegrado);

    function trocarAba(novaAba) {
        setAba(novaAba);
        setFiltros(FILTROS_INICIAIS);
        limpar();
    }

    const elegiveisConfirmar = somenteLeitura ? [] : itensSelecionados.filter(
        (item) => item.statusOS === "G" && item.completo && item.osId
    );
    const elegiveisIntegrar = somenteLeitura ? [] : itensSelecionados.filter(
        (item) => item.statusOS === "C" && item.osId
    );

    const diasFaturamento = useMemo(
        () => [...new Set(itens.map((i) => i.diaFaturamento).filter((d) => d != null))].sort((a, b) => a - b),
        [itens]
    );
    const diasVencimento = useMemo(
        () => [...new Set(itens.map((i) => i.diaVencimento).filter((d) => d != null))].sort((a, b) => a - b),
        [itens]
    );

    function recarregar() {
        limpar();
        carregarDados(aba);
    }

    async function salvarCodigoEmFaturamentos(faturamentoIds, corpo) {
        for (const faturamentoId of faturamentoIds) {
            await atualizarCodigoIntegracao(faturamentoId, corpo);
        }
        recarregar();
    }

    const acoesEdicao = {
        onSalvarCodigoCliente: (item, valor) =>
            salvarCodigoEmFaturamentos(item.servicos.map((s) => s.faturamentoId), { codigoIntegracaoSISDM: valor }),
        onSalvarCampoServico: (servico, corpo) =>
            salvarCodigoEmFaturamentos([servico.faturamentoId], corpo),
        onSalvarCodigoRevenda: (item, valor) =>
            salvarCodigoEmFaturamentos(item.faturamentoIds, { codigoIntegracaoSISDM: valor }),
        onSalvarObservacaoNotaFiscalRevenda: (item, valor) =>
            salvarCodigoEmFaturamentos(item.faturamentoIds, { observacaoNotaFiscal: valor }),
        onSalvarCodigoServicoRevenda: (servico, valor) =>
            salvarCodigoEmFaturamentos(servico.faturamentoIds, { servicoSISDM: valor }),
        onSalvarObservacaoNotaFiscalServicoRevenda: (servico, valor) =>
            salvarCodigoEmFaturamentos(servico.faturamentoIds, { observacaoNotaFiscal: valor }),
        onSalvarTipoServicoRevenda: (servico, tipoServicoId) =>
            salvarCodigoEmFaturamentos(servico.faturamentoIds, { tipoServicoPortalId: tipoServicoId })
    };

    function abrirLote(itensLote, novoStatus) {
        if (itensLote.length === 0 || somenteLeitura) return;
        setLote({ itens: itensLote, novoStatus });
    }

    function handleIntegrarLote() {
        if (inconsistencias.length > 0) {
            setIntegrarAposInconsistencias(true);
            setShowInconsistencias(true);
            return;
        }
        abrirLote(elegiveisIntegrar, "I");
    }

    function continuarAposInconsistencias() {
        setShowInconsistencias(false);
        if (integrarAposInconsistencias) abrirLote(elegiveisIntegrar, "I");
        setIntegrarAposInconsistencias(false);
    }

    function fecharLote(recarregarDados) {
        setLote(null);
        if (recarregarDados) {
            recarregar();
        }
    }

    return (
        <Box sx={{ display: "flex", flexDirection: "column", minHeight: "100vh", backgroundColor: "ghostwhite" }}>
            <Box sx={{ px: 4, pt: 3, pb: 1, flexShrink: 0, display: "flex", justifyContent: "space-between", alignItems: "center", gap: 2, flexWrap: "wrap" }}>
                <ButtonBase variant="text" onClick={voltar}>
                    <KeyboardBackspaceIcon fontSize="small" sx={{ mr: 0.5 }} />
                    Voltar para a seleção de período
                </ButtonBase>

                <Typography component="h1" variant="h5" sx={{ fontWeight: 700, color: "#132350" }}>
                    Integração — {formatarPeriodoExtenso(periodo)}
                </Typography>

                <Box sx={{ minWidth: { sm: "18rem" } }} />
            </Box>

            <Box sx={{ flex: 1, px: 4, pb: 10, pt: 1 }}>

                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    {pularAutoGeracaoRef.current
                        ? "Exibindo os itens já salvos deste período — nenhuma OS nova será gerada automaticamente."
                        : "As OS do período são geradas automaticamente — revise, confirme e integre no SISDM."}
                </Typography>

                <FiltrosIntegracao
                    filtros={filtros}
                    onChange={setFiltros}
                    diasFaturamento={diasFaturamento}
                    diasVencimento={diasVencimento}
                    aba={aba}
                />

                {progressoGeracao && (
                    <Box sx={{ mb: 3 }}>
                        <Typography variant="caption" color="text.secondary">
                            Gerando OS no portal... ({progressoGeracao.feitas} de {progressoGeracao.total})
                        </Typography>
                        <LinearProgress
                            variant="determinate"
                            value={progressoGeracao.total === 0 ? 100 : Math.round((progressoGeracao.feitas / progressoGeracao.total) * 100)}
                            sx={{ mt: 0.5, borderRadius: 999 }}
                            aria-label="Progresso da geração automática"
                        />
                    </Box>
                )}

                <Box sx={{
                    backgroundColor: "white",
                    borderRadius: "20px",
                    border: `1px solid ${BORDA_SUAVE}`,
                    boxShadow: SOMBRA_CARD,
                    overflow: "hidden"
                }}>
                    <Box sx={{ px: { xs: 2, md: 2.5 }, pt: { xs: 1.5, md: 2 }, pb: 1, borderBottom: `1px solid ${BORDA_SUAVE}` }}>
                        <Tabs
                            value={aba}
                            onChange={(_, valor) => trocarAba(valor)}
                            TabIndicatorProps={{ style: { display: "none" } }}
                            sx={{
                                minHeight: 0,
                                "& .MuiTabs-flexContainer": { gap: 1 },
                                "& .MuiTab-root": {
                                    minHeight: 0,
                                    minWidth: 0,
                                    py: 0.75,
                                    px: 2,
                                    textTransform: "none",
                                    fontWeight: 600,
                                    fontSize: "0.875rem",
                                    color: "#5A6B8C",
                                    borderRadius: "10px",
                                    transition: "background-color 0.2s ease, color 0.2s ease"
                                },
                                "& .MuiTab-root:hover": {
                                    backgroundColor: "rgba(19, 35, 80, 0.04)"
                                },
                                "& .Mui-selected": {
                                    backgroundColor: "rgba(19, 35, 80, 0.08)",
                                    color: `${NAVY} !important`
                                }
                            }}
                        >
                            <Tab label="BMA" value="bma" disableRipple />
                            <Tab label="Revendas" value="canais" disableRipple />
                        </Tabs>
                    </Box>

                    {loading ? (
                        <Box sx={{ py: 4 }}>
                            <Loading />
                            <Typography variant="body2" color="text.secondary" align="center" sx={{ mt: 1 }}>
                                Carregando integrações...
                            </Typography>
                        </Box>
                    ) : aba === "bma" ? (
                        <TabelaIntegracaoBma
                            itens={itensFiltrados}
                            selecao={selecao}
                            tiposServico={tiposServico}
                            somenteLeitura={somenteLeitura}
                            onSalvarCodigoCliente={acoesEdicao.onSalvarCodigoCliente}
                            onSalvarCampoServico={acoesEdicao.onSalvarCampoServico}
                            onConfirmar={(item) => abrirLote([item], "C")}
                            onIntegrar={(item) => abrirLote([item], "I")}
                            onDesconfirmar={(item) => abrirLote([item], "G")}
                            onIgnorar={(item) => abrirLote([item], "X")}
                            onReconsiderar={(item) => abrirLote([item], "G")}
                            onVerHistorico={(item, anchor) => setHistorico({ osId: item.osId, anchorEl: anchor })}
                        />
                    ) : (
                        <TabelaIntegracaoRevendas
                            itens={itensFiltrados}
                            selecao={selecao}
                            tiposServico={tiposServico}
                            somenteLeitura={somenteLeitura}
                            onSalvarCodigoRevenda={acoesEdicao.onSalvarCodigoRevenda}
                            onSalvarObservacaoNotaFiscal={acoesEdicao.onSalvarObservacaoNotaFiscalRevenda}
                            onSalvarCodigoServico={acoesEdicao.onSalvarCodigoServicoRevenda}
                            onSalvarObservacaoNotaFiscalServico={acoesEdicao.onSalvarObservacaoNotaFiscalServicoRevenda}
                            onSalvarTipoServico={acoesEdicao.onSalvarTipoServicoRevenda}
                            onVerClientes={(revenda, servico) => setClientesRevenda({ revenda, servico })}
                            onConfirmar={(item) => abrirLote([item], "C")}
                            onIntegrar={(item) => abrirLote([item], "I")}
                            onDesconfirmar={(item) => abrirLote([item], "G")}
                            onIgnorar={(item) => abrirLote([item], "X")}
                            onReconsiderar={(item) => abrirLote([item], "G")}
                            onVerHistorico={(item, anchor) => setHistorico({ osId: item.osId, anchorEl: anchor })}
                        />
                    )}
                </Box>
            </Box>

            <Box sx={{
                position: "sticky",
                bottom: 0,
                zIndex: 2,
                flexShrink: 0,
                display: "flex",
                justifyContent: "flex-end",
                alignItems: "center",
                gap: 2,
                flexWrap: "wrap",
                px: 4,
                py: 2,
                backgroundColor: "white",
                borderTop: "1px solid #E6EAF2",
                boxShadow: "0px -2px 8px rgba(19, 35, 80, 0.06)"
            }}>
                {somenteLeitura ? (
                    <Typography variant="body2" sx={{ fontWeight: 600, color: "#2e7d32" }}>
                        Período totalmente integrado ao SISDM — somente consulta.
                    </Typography>
                ) : (
                    <Stack direction="row" spacing={2} alignItems="center">
                        {inconsistencias.length > 0 && (
                            <Button
                                variant="text"
                                startIcon={
                                    <Badge badgeContent={inconsistencias.length} color="warning">
                                        <WarningAmberIcon />
                                    </Badge>
                                }
                                onClick={() => {
                                    setIntegrarAposInconsistencias(false);
                                    setShowInconsistencias(true);
                                }}
                                sx={{ textTransform: "none", color: "#b26a00" }}
                            >
                                Ver Inconsistências
                            </Button>
                        )}

                        <ButtonBase
                            variant="outlined"
                            startIcon={<CheckCircleOutlineIcon />}
                            disabled={elegiveisConfirmar.length === 0}
                            onClick={() => abrirLote(elegiveisConfirmar, "C")}
                        >
                            Confirmar
                        </ButtonBase>

                        <ButtonBase
                            variant="contained"
                            startIcon={<SyncOutlinedIcon />}
                            disabled={elegiveisIntegrar.length === 0}
                            onClick={handleIntegrarLote}
                        >
                            Integrar
                        </ButtonBase>
                    </Stack>
                )}
            </Box>

            <ModalInconsistencias
                show={showInconsistencias}
                onHide={() => {
                    setShowInconsistencias(false);
                    setIntegrarAposInconsistencias(false);
                }}
                onContinuar={continuarAposInconsistencias}
                periodo={periodo}
                itens={inconsistencias}
            />

            <ModalLoteStatus
                open={Boolean(lote)}
                onClose={fecharLote}
                itens={lote?.itens || []}
                novoStatus={lote?.novoStatus}
                periodo={periodo}
                usuario={usuarioNomeRef.current}
            />

            <ModalClientesRevenda
                open={Boolean(clientesRevenda)}
                onClose={() => setClientesRevenda(null)}
                periodo={periodo}
                revenda={clientesRevenda?.revenda}
                servico={clientesRevenda?.servico}
            />

            <PopoverHistoricoOS
                anchorEl={historico?.anchorEl}
                osId={historico?.osId}
                onClose={() => setHistorico(null)}
            />
        </Box>
    );
};

export default IntegracaoSisdm;
